/**
 * Window Functions Module
 * Sprint 12: Extracted from inline JavaScript in index.html
 * Sprint 31: Updated for environment-aware communication
 *
 * Purpose: Global window functions for onclick handlers and UI interactions
 *
 * Design Pattern: Facade Pattern
 * - Provides simplified interface to complex subsystems (appCore modules)
 * - Exposes functions globally on window object for HTML onclick handlers
 */

// Sprint 36: Import callCanister for Ada communication
import { callCanister } from '../api/communication';
import { environment } from '../config/environment';

// Declare window interface extensions
declare global {
    interface Window {
        showPage: (pageId: string) => void;
        showDeviceTable: (type: string) => void;
        adaConversationId: string | null;
        showAI: () => Promise<void>;
        hideAI: () => void;
        refreshDevices: () => void;
        exportDevices: () => Promise<void>;
        sortDevicesTable: (column: string) => void;
        showScheduleModal: () => void;
        hideScheduleModal: () => void;
        startDiscoveryScan: () => void;
        downloadDiscoveryLog: () => void;
        clearDiscoveryLog: () => void;
        downloadServiceMapLog: () => Promise<void>;
        clearServiceMapLog: () => void;
        refreshApplications: () => void;
        exportApplications: () => void;
        showAddApplicationModal: () => void;
        applyApplicationFilters: () => void;
        sortApplicationsTable: (column: string) => void;
        refreshRelationships: () => void;
        exportRelationships: () => void;
        filterRelationships: () => void;
        sortRelationshipsTable: (column: string) => void;
        toggleAllCredentialSelection: () => void;
        updateDeleteButtonState: () => void;
        deleteSelectedCredentials: () => Promise<void>;
        showAddCredentialModal: () => void;
        hideAddCredentialModal: () => void;
        sortCredentials: (column: string) => void;
        viewCredentialDetails: (credentialId: string) => void;
        revokeCredential: (credentialId: string) => Promise<void>;
        updatePermissionsForType: () => void;
        generateCredential: () => Promise<void>;
        refreshServiceModels: () => void;
        showAddServiceModal: () => void;
        showServiceMap: (service: string) => void;
        hideServiceMap: () => void;
        runServiceDiscovery: () => Promise<void>;
        resetCytoscapeView: () => void;
        exportServiceMap: () => void;
        showCMDBRecord: (id: string, name: string, ip: string) => void;
        hideCMDBModal: () => void;
        showCMDBTab: (tab: string) => void;
        hideDeviceTable: () => void;
        handleAIKeyPress: (event: KeyboardEvent) => void;
        sendAIMessage: () => Promise<void>;
        clearAIConversation: () => void;
        clearVectorDatabase: () => Promise<void>;
        editSchedule: (id: string) => void;
        deleteSchedule: (id: string) => void;
        saveSchedule: () => void;
        serviceMapController: any;
        appCore: any;
        canisterClient: any;
        iiAgent: any;
        showToastNotification: (message: string, type: string) => void;
    }
}

/**
 * Initialize all window functions
 * Called from main.ts during application initialization
 */
export function initializeWindowFunctions(): void {
    console.log('🔧 Initializing window functions...');

    // Core navigation function
    window.showPage = function(pageId: string): void {
        console.log('🔄 Navigation to:', pageId);

        // Hide all pages
        const pages = document.querySelectorAll('[id$="-page"]');
        pages.forEach(page => {
            page.classList.add('hidden');
        });

        // Show selected page
        const targetPage = document.getElementById(pageId + '-page');
        if (targetPage) {
            targetPage.classList.remove('hidden');
            console.log('✅ Showing page:', pageId);
        } else {
            // Fallback to dashboard
            const dashboardPage = document.getElementById('dashboard-page');
            if (dashboardPage) {
                dashboardPage.classList.remove('hidden');
            }
        }

        // Update sidebar active state
        const sidebarItems = document.querySelectorAll('.sidebar-item');
        sidebarItems.forEach(item => {
            item.classList.remove('active');
            if ((item as HTMLElement).dataset && (item as HTMLElement).dataset.page === pageId) {
                item.classList.add('active');
            }
        });
    };

    window.showDeviceTable = function(type: string): void {
        console.log('Showing device table for:', type);
        // Use the page manager's method for real device data
        if (window.appCore && window.appCore.getModule('pageManager')) {
            window.appCore.getModule('pageManager').showDeviceTable(type);
        } else {
            console.warn('⚠️ PageManager not available, falling back to simple modal');
            const modal = document.getElementById('device-table-modal');
            if (modal) {
                modal.classList.remove('hidden');
            }
        }
    };

    // Ada conversation management
    window.adaConversationId = null;

    window.showAI = async function(): Promise<void> {
        console.log('Showing AI assistant');
        const modal = document.getElementById('ai-modal');
        if (modal) {
            modal.classList.remove('hidden');

            // Start a new Ada conversation if we don't have one
            if (!window.adaConversationId) {
                try {
                    const result = await callCanister<{ Ok?: string; Err?: string }>(
                        'ada', 'start_conversation', ['AI Assistant Session']
                    );
                    if (result.Ok) {
                        window.adaConversationId = result.Ok;
                        console.log('Started Ada conversation:', window.adaConversationId);
                    }
                } catch (error) {
                    console.error('Failed to start Ada conversation:', error);
                }
            }
        }
    };

    window.hideAI = function(): void {
        console.log('Hiding AI assistant');
        const modal = document.getElementById('ai-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    };

    window.refreshDevices = function(): void {
        console.log('Refreshing devices...');
        location.reload();
    };

    window.exportDevices = async function(): Promise<void> {
        console.log('Exporting devices...');

        const canisterClient = window.canisterClient || window.appCore?.getModule('canisterClient');
        if (!canisterClient) {
            alert('❌ System not ready. Please wait and try again.');
            return;
        }

        try {
            // Fetch all devices
            const result = await canisterClient.callCanister('vector_db', 'get_all_devices', []);

            if (!result || !result.raw) {
                alert('❌ No devices found to export');
                return;
            }

            // Parse devices from Candid response
            const devices = parseDevicesFromRaw(result.raw);

            if (devices.length === 0) {
                alert('❌ No devices found to export');
                return;
            }

            // Create CSV content
            const headers = ['Device ID', 'Device Name', 'Type', 'IP Address', 'Operating System', 'Model/Version', 'Status'];
            const csvRows = [headers.join(',')];

            devices.forEach((device: any) => {
                const row = [
                    escapeCSV(device.device_id || ''),
                    escapeCSV(device.hostname || device.device_id || 'Unknown'),
                    escapeCSV(device.device_type || 'Unknown'),
                    escapeCSV(device.ip || device.device_id || 'N/A'),
                    escapeCSV(device.os || 'Unknown'),
                    escapeCSV(device.model || device.version || 'N/A'),
                    escapeCSV(device.status || 'Unknown')
                ];
                csvRows.push(row.join(','));
            });

            const csvContent = csvRows.join('\n');

            // Create download link
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
            link.setAttribute('href', url);
            link.setAttribute('download', `asd_devices_${timestamp}.csv`);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Show success notification
            if (window.appCore?.getModule('toastNotifications')) {
                window.appCore.getModule('toastNotifications').show(`✅ Exported ${devices.length} devices to CSV`, 'success');
            }

            console.log(`✅ Exported ${devices.length} devices to CSV`);

        } catch (error: any) {
            console.error('❌ Error exporting devices:', error);
            alert('❌ Error exporting devices: ' + error.message);
        }
    };

    window.sortDevicesTable = function(column: string): void {
        console.log('sortDevicesTable stub called, waiting for app.js to load...', column);
    };

    window.showScheduleModal = function(): void {
        console.log('Showing schedule modal');
        const modal = document.getElementById('schedule-modal');
        if (modal) {
            modal.classList.remove('hidden');
        }
    };

    window.hideScheduleModal = function(): void {
        console.log('Hiding schedule modal');
        const modal = document.getElementById('schedule-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    };

    window.startDiscoveryScan = function(): void {
        console.log('Starting discovery scan...');
        const btn = document.getElementById('start-discovery-btn') as HTMLButtonElement;
        if (btn) {
            btn.textContent = 'Scanning...';
            btn.disabled = true;
        }
        setTimeout(() => {
            if (btn) {
                btn.textContent = 'Start Discovery';
                btn.disabled = false;
            }
        }, 3000);
    };

    // NOTE: downloadDiscoveryLog and clearDiscoveryLog are defined in
    // discovery/discovery-logging.ts - do NOT define stubs here as they
    // would overwrite the real implementations due to load order.
    // Owner: discovery-logging.ts (DiscoveryLogger class)

    // Service Map Log functions
    window.downloadServiceMapLog = async function(): Promise<void> {
        const logContainer = document.getElementById('log-container');
        if (!logContainer) {
            console.warn('Service map log container not found');
            return;
        }

        // Get all log entries
        const logEntries = logContainer.querySelectorAll('.log-entry');
        if (logEntries.length === 0) {
            console.warn('No log entries to download');
            alert('No log entries to download');
            return;
        }

        // Get timezone-aware timestamp
        let timestamp: string;
        try {
            if (window.canisterClient && typeof window.canisterClient.getTimezoneConfig === 'function') {
                console.log('🔍 Fetching timezone config...');
                const response = await window.canisterClient.getTimezoneConfig();
                console.log('🔍 Timezone config response:', response);

                // Parse from raw Candid string if data is empty
                let offsetHours: number, label: string;
                if (response.raw) {
                    console.log('🔍 Parsing from raw Candid:', response.raw.substring(0, 200));
                    // Extract utc_offset_hours: "utc_offset_hours = -8 : int32"
                    const offsetMatch = response.raw.match(/utc_offset_hours\s*=\s*(-?\d+)/);
                    // Extract label: "label = \"PST\""
                    const labelMatch = response.raw.match(/label\s*=\s*"([^"]+)"/);

                    if (offsetMatch && labelMatch) {
                        offsetHours = parseInt(offsetMatch[1], 10);
                        label = labelMatch[1];
                        console.log(`📅 Parsed from Candid - Offset: ${offsetHours}, Label: ${label}`);
                    } else {
                        throw new Error('Failed to parse timezone from Candid response');
                    }
                } else {
                    // Try data field as fallback
                    const config = response.data || response;
                    offsetHours = Number(config.utc_offset_hours);
                    label = String(config.label || 'UTC');
                }

                if (isNaN(offsetHours)) {
                    throw new Error('Invalid UTC offset hours');
                }

                const now = new Date();
                const offsetMillis = offsetHours * 60 * 60 * 1000;
                const adjustedTime = new Date(now.getTime() + offsetMillis);

                const year = adjustedTime.getUTCFullYear();
                const month = String(adjustedTime.getUTCMonth() + 1).padStart(2, '0');
                const day = String(adjustedTime.getUTCDate()).padStart(2, '0');
                const hours = String(adjustedTime.getUTCHours()).padStart(2, '0');
                const minutes = String(adjustedTime.getUTCMinutes()).padStart(2, '0');
                const seconds = String(adjustedTime.getUTCSeconds()).padStart(2, '0');

                timestamp = `${year}-${month}-${day}T${hours}-${minutes}-${seconds}${label}`;
                console.log(`📅 Using timezone-aware timestamp: ${timestamp} (${label})`);
            } else {
                // Fallback to UTC if canisterClient not available
                console.warn('⚠️ CanisterClient not available, using UTC timestamp');
                timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
            }
        } catch (error) {
            console.error('❌ Failed to get timezone config, using UTC:', error);
            timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        }

        // Build log content
        const logContent = Array.from(logEntries)
            .map(entry => (entry as HTMLElement).innerText || entry.textContent)
            .join('\n');

        // Create download with UTF-8 encoding to preserve emojis
        const blob = new Blob([logContent], { type: 'text/plain; charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `service-map-log-${timestamp}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        console.log('📥 Service map log downloaded successfully');
    };

    window.clearServiceMapLog = function(): void {
        const logContainer = document.getElementById('log-container');
        if (logContainer) {
            logContainer.innerHTML = '<div class="text-gray-500 italic">Log cleared. Waiting for service discovery to start...</div>';
            console.log('🧹 Service map log cleared');
        }
    };

    window.refreshApplications = function(): void {
        console.log('refreshApplications stub called, waiting for app.js to load...');
    };

    window.exportApplications = function(): void {
        console.log('exportApplications stub called, waiting for app.js to load...');
    };

    window.showAddApplicationModal = function(): void {
        console.log('showAddApplicationModal stub called, waiting for app.js to load...');
    };

    window.applyApplicationFilters = function(): void {
        console.log('applyApplicationFilters stub called, waiting for app.js to load...');
    };

    window.sortApplicationsTable = function(column: string): void {
        console.log('sortApplicationsTable stub called, waiting for app.js to load...', column);
    };

    // Relationships page functions
    window.refreshRelationships = function(): void {
        console.log('refreshRelationships stub called, waiting for app.js to load...');
        if (window.appCore?.getModule('pageManager')) {
            window.appCore.getModule('pageManager').loadRelationshipsDataForPage();
        }
    };

    window.exportRelationships = function(): void {
        console.log('exportRelationships stub called, waiting for app.js to load...');
    };

    window.filterRelationships = function(): void {
        console.log('filterRelationships stub called, waiting for app.js to load...');
    };

    window.sortRelationshipsTable = function(column: string): void {
        console.log('sortRelationshipsTable stub called, waiting for app.js to load...', column);
    };

    // Credential selection and deletion functions
    window.toggleAllCredentialSelection = function(): void {
        const selectAll = document.getElementById('select-all-credentials') as HTMLInputElement;
        const checkboxes = document.querySelectorAll('.credential-checkbox') as NodeListOf<HTMLInputElement>;

        checkboxes.forEach(checkbox => {
            checkbox.checked = selectAll.checked;
        });

        window.updateDeleteButtonState();
    };

    window.updateDeleteButtonState = function(): void {
        const selectedCheckboxes = document.querySelectorAll('.credential-checkbox:checked');
        const deleteBtn = document.getElementById('delete-selected-btn');
        const selectedCount = document.getElementById('selected-count');

        if (deleteBtn && selectedCount) {
            const count = selectedCheckboxes.length;
            selectedCount.textContent = String(count);

            if (count > 0) {
                deleteBtn.classList.remove('hidden');
                (deleteBtn as HTMLButtonElement).disabled = false;
            } else {
                deleteBtn.classList.add('hidden');
                (deleteBtn as HTMLButtonElement).disabled = true;
            }
        }
    };

    window.deleteSelectedCredentials = async function(): Promise<void> {
        const selectedCheckboxes = document.querySelectorAll('.credential-checkbox:checked');
        const credentialIds = Array.from(selectedCheckboxes).map(cb => (cb as HTMLElement).dataset.credentialId);

        if (credentialIds.length === 0) {
            return;
        }

        const confirmMessage = credentialIds.length === 1
            ? 'Are you sure you want to delete this credential?'
            : `Are you sure you want to delete ${credentialIds.length} credentials?`;

        if (!confirm(confirmMessage)) {
            return;
        }

        console.log('Deleting credentials:', credentialIds);

        // Delete from localStorage (mock implementation)
        const storedCredentials = JSON.parse(localStorage.getItem('mock_credentials') || '[]');
        const remainingCredentials = storedCredentials.filter((cred: any) => !credentialIds.includes(cred.id));
        localStorage.setItem('mock_credentials', JSON.stringify(remainingCredentials));

        // Show success message
        if (window.appCore?.getModule('toastNotifications')) {
            const message = credentialIds.length === 1
                ? 'Credential deleted successfully'
                : `${credentialIds.length} credentials deleted successfully`;
            window.appCore.getModule('toastNotifications').show(message, 'success');
        }

        // Refresh the credentials list
        if (window.appCore?.getModule('pageManager')) {
            window.appCore.getModule('pageManager').loadCredentialsData();
        }

        // Reset selection
        const selectAll = document.getElementById('select-all-credentials') as HTMLInputElement;
        if (selectAll) selectAll.checked = false;
        window.updateDeleteButtonState();
    };

    window.showAddCredentialModal = function(): void {
        console.log('Showing add credential modal');
        const modal = document.getElementById('add-credential-modal');
        if (modal) {
            modal.classList.remove('hidden');
        }
    };

    window.hideAddCredentialModal = function(): void {
        console.log('Hiding add credential modal');
        const modal = document.getElementById('add-credential-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    };

    window.sortCredentials = function(column: string): void {
        console.log('sortCredentials stub called, waiting for app.js to load...', column);
    };

    window.viewCredentialDetails = function(credentialId: string): void {
        console.log('Viewing credential details for:', credentialId);
        alert(`Credential details for ${credentialId} - Feature coming soon!`);
    };

    window.revokeCredential = async function(credentialId: string): Promise<void> {
        console.log('Revoking credential:', credentialId);
        if (confirm(`Are you sure you want to revoke credential ${credentialId}?`)) {
            try {
                console.log('Revoking credential:', credentialId);

                if (window.appCore?.getModule('toastNotifications')) {
                    window.appCore.getModule('toastNotifications').show(
                        `Credential ${credentialId} revoked successfully.`,
                        'success'
                    );
                }

                // Refresh the credentials list
                if (window.appCore?.getModule('pageManager')) {
                    window.appCore.getModule('pageManager').loadCredentialsData();
                }
            } catch (error: any) {
                console.error('Error revoking credential:', error);
                alert(`Error revoking credential: ${error.message}`);
            }
        }
    };

    window.updatePermissionsForType = function(): void {
        console.log('Updating permissions for credential type...');
        const credentialType = (document.getElementById('credential-type') as HTMLSelectElement)?.value;
        const permissionsContainer = document.getElementById('credential-permissions');

        if (!permissionsContainer) return;

        // Clear existing permissions
        const checkboxes = permissionsContainer.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
        checkboxes.forEach(cb => cb.checked = false);

        // Set default permissions based on type
        switch(credentialType) {
            case 'Windows':
                const winrmBox = document.getElementById('perm-winrm') as HTMLInputElement;
                const rdpBox = document.getElementById('perm-rdp') as HTMLInputElement;
                if (winrmBox) winrmBox.checked = true;
                if (rdpBox) rdpBox.checked = true;
                break;
            case 'Linux':
                const sshBox = document.getElementById('perm-ssh') as HTMLInputElement;
                if (sshBox) sshBox.checked = true;
                break;
            case 'Network':
                const snmpBox = document.getElementById('perm-snmp') as HTMLInputElement;
                const sshNetBox = document.getElementById('perm-ssh') as HTMLInputElement;
                if (snmpBox) snmpBox.checked = true;
                if (sshNetBox) sshNetBox.checked = true;
                break;
            case 'Storage':
                const apiBox = document.getElementById('perm-api') as HTMLInputElement;
                if (apiBox) apiBox.checked = true;
                break;
        }
    };

    window.generateCredential = async function(): Promise<void> {
        console.log('Generating credential...');

        // Get form values
        const credentialName = (document.getElementById('credential-name') as HTMLInputElement)?.value;
        const credentialType = (document.getElementById('credential-type') as HTMLSelectElement)?.value;
        const targetDevice = (document.getElementById('target-ip') as HTMLInputElement)?.value;
        const expiryMinutes = (document.getElementById('expiration-time') as HTMLInputElement)?.value;

        console.log('Form values:', {
            credentialName,
            credentialType,
            targetDevice,
            expiryMinutes
        });

        if (!credentialName || !credentialType || !targetDevice) {
            console.error('Missing required fields:', {
                hasName: !!credentialName,
                hasType: !!credentialType,
                hasTarget: !!targetDevice
            });
            alert('Please fill in all required fields');
            return;
        }

        // Get selected permissions
        const permissions: string[] = [];
        const checkboxes = document.querySelectorAll('#credential-permissions input[type="checkbox"]:checked');
        checkboxes.forEach(cb => {
            const label = cb.nextElementSibling?.textContent;
            if (label) permissions.push(label);
        });

        try {
            // Actually store the credential in the canister
            if (window.canisterClient) {
                const credentialData = {
                    name: credentialName,
                    credential_type: credentialType,
                    target: targetDevice,
                    permissions: permissions
                };

                // Convert expiry minutes to nanoseconds for the canister
                const durationNanos = BigInt(parseInt(expiryMinutes) * 60 * 1000000000);

                console.log('Calling canister to generate credential:', credentialData);
                const result = await window.canisterClient.generateJitCredential(
                    credentialType,
                    targetDevice,
                    durationNanos,
                    credentialName
                );

                console.log('Credential generation result:', result);

                if (result && result.ok) {
                    window.hideAddCredentialModal();

                    // Show success message
                    if (window.appCore?.getModule('toastNotifications')) {
                        window.appCore.getModule('toastNotifications').show(
                            `JIT credential "${credentialName}" generated successfully! ID: ${result.ok.id}. Expires in ${expiryMinutes} minutes.`,
                            'success'
                        );
                    } else {
                        alert(`JIT credential "${credentialName}" generated successfully! Expires in ${expiryMinutes} minutes.`);
                    }

                    // Refresh the credentials list
                    if (window.appCore?.getModule('pageManager')) {
                        window.appCore.getModule('pageManager').loadCredentialsData();
                    }
                } else {
                    const error = result?.err || 'Unknown error';
                    console.error('Failed to generate credential:', error);
                    alert(`Failed to generate credential: ${error}`);
                }
            } else {
                console.error('Canister client not available');
                alert('Unable to connect to credential manager. Please refresh the page.');
            }
        } catch (error: any) {
            console.error('Error generating credential:', error);
            alert(`Error generating credential: ${error.message}`);
        }
    };

    window.refreshServiceModels = async function(): Promise<void> {
        console.log('🔄 Refreshing service models from vector_db...');
        if (window.serviceMapController) {
            await window.serviceMapController.displayActiveServiceModels();
        } else {
            console.warn('⚠️ ServiceMapController not available');
        }
    };

    window.showAddServiceModal = function(): void {
        console.log('showAddServiceModal stub called, waiting for app.js to load...');
    };

    // Service Map and ServiceWatch Discovery Functions
    window.showServiceMap = function(service: string): void {
        console.log('🗺️ Opening service map for:', service);

        // Delegate to PageManager if available
        if (window.appCore && window.appCore.getModule && window.appCore.getModule('pageManager')) {
            const pageManager = window.appCore.getModule('pageManager');
            if (pageManager && pageManager.showServiceMap) {
                pageManager.showServiceMap(service);
                return;
            }
        }

        // Fallback: just show the modal
        const modal = document.getElementById('service-map-modal');
        if (modal) {
            modal.classList.remove('hidden');
            // Reset discovery status
            const statusBar = document.getElementById('discovery-status-bar');
            if (statusBar) statusBar.classList.add('hidden');
            const progressBar = document.getElementById('discovery-progress-bar');
            if (progressBar) (progressBar as HTMLElement).style.width = '0%';
        }
    };

    window.hideServiceMap = function(): void {
        console.log('Hiding service map');
        const modal = document.getElementById('service-map-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    };

    // Global ServiceMapController instance (OOA/OOD pattern)
    window.serviceMapController = null;

    // Initialize controller when iiAgent is ready
    function initServiceMapController(): void {
        if (!window.serviceMapController && window.iiAgent) {
            console.log('🎛️ Creating ServiceMapController...');
            // ServiceMapController is imported elsewhere
            // window.serviceMapController = new ServiceMapController(window.iiAgent);
        }

        // Always ensure visualizer is initialized when modal is shown
        if (window.serviceMapController) {
            console.log('🎨 Ensuring visualizer is initialized...');
            window.serviceMapController.initialize('cy');
        }
    }

    // Main service discovery function
    window.runServiceDiscovery = async function(): Promise<void> {
        console.log('🔍 Starting service discovery via ServiceMapController...');

        // Ensure modal is open BEFORE initializing visualizer
        const modal = document.getElementById('service-map-modal');
        if (modal && modal.classList.contains('hidden')) {
            modal.classList.remove('hidden');
            // Wait for DOM to update
            await new Promise(resolve => setTimeout(resolve, 50));
        }

        // Initialize controller if needed (now that modal is visible)
        initServiceMapController();

        if (!window.serviceMapController) {
            console.error('❌ ServiceMapController not initialized. iiAgent may not be ready.');
            if (window.showToastNotification) {
                window.showToastNotification('Service map controller not ready', 'error');
            }
            return;
        }

        // Gather UI elements
        const uiElements = {
            statusBar: document.getElementById('discovery-status-bar'),
            statusText: document.getElementById('discovery-status-text'),
            progressText: document.getElementById('discovery-progress-text'),
            progressBar: document.getElementById('discovery-progress-bar'),
            runBtn: document.getElementById('run-discovery-btn'),
            statsDiv: document.getElementById('service-map-stats')
        };

        // Run discovery workflow with live synchronized visualization
        await window.serviceMapController.runDiscoveryLive('unboundata', uiElements);
    };

    // Helper function to reset Cytoscape view
    window.resetCytoscapeView = function(): void {
        if (window.appCore && window.appCore.getModule && window.appCore.getModule('pageManager')) {
            const pageManager = window.appCore.getModule('pageManager');
            if (pageManager && pageManager.resetServiceMapView) {
                pageManager.resetServiceMapView();
                return;
            }
        }

        // Fallback to old controller
        if (window.serviceMapController) {
            window.serviceMapController.resetView();
        }
    };

    // Helper function to export service map as PNG
    window.exportServiceMap = function(): void {
        if (window.appCore && window.appCore.getModule && window.appCore.getModule('pageManager')) {
            const pageManager = window.appCore.getModule('pageManager');
            if (pageManager && pageManager.exportServiceMap) {
                pageManager.exportServiceMap();
                return;
            }
        }

        // Fallback to old controller
        if (window.serviceMapController) {
            window.serviceMapController.exportImage();
        } else {
            alert('No service map to export. Please run discovery first.');
        }
    };

    window.showCMDBRecord = function(id: string, name: string, ip: string): void {
        console.log('📋 Opening CMDB record for:', { id, name, ip });
        const modal = document.getElementById('cmdb-modal');
        if (modal) {
            // Update modal title and content
            const titleEl = document.querySelector('#cmdb-modal h2');
            if (titleEl) titleEl.textContent = `CMDB Record: ${name}`;

            // Populate basic info
            const infoContent = document.getElementById('cmdb-record-basic');
            if (infoContent) {
                const deviceType = id.includes('lb') ? 'Load Balancer' : id.includes('db') ? 'Database' : id.includes('srv') ? 'Application Server' : 'Infrastructure';
                infoContent.innerHTML = `
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Device ID</label>
                            <div class="mt-1 text-sm text-gray-900">${id}</div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Name</label>
                            <div class="mt-1 text-sm text-gray-900">${name}</div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700">IP Address / URL</label>
                            <div class="mt-1 text-sm text-gray-900">${ip}</div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Status</label>
                            <div class="mt-1">
                                <span class="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Online</span>
                            </div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Type</label>
                            <div class="mt-1 text-sm text-gray-900">${deviceType}</div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Last Updated</label>
                            <div class="mt-1 text-sm text-gray-900">${new Date().toLocaleString()}</div>
                        </div>
                    </div>
                `;
            }

            modal.classList.remove('hidden');
        }
    };

    window.hideCMDBModal = function(): void {
        console.log('Hiding CMDB modal');
        const modal = document.getElementById('cmdb-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    };

    window.showCMDBTab = function(tab: string): void {
        console.log('showCMDBTab stub called, waiting for app.js to load...', tab);
    };

    window.hideDeviceTable = function(): void {
        console.log('Hiding device table');
        const modal = document.getElementById('device-table-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    };

    window.handleAIKeyPress = function(event: KeyboardEvent): void {
        // Check if Enter key was pressed
        if (event.key === 'Enter' || event.keyCode === 13) {
            event.preventDefault(); // Prevent form submission if in a form
            window.sendAIMessage();
        }
    };

    // Clear Vector Database function
    window.clearVectorDatabase = async function(): Promise<void> {
        const button = document.getElementById('clear-vdb-btn') as HTMLButtonElement;

        // Confirmation dialog
        if (!confirm('⚠️ WARNING: This will permanently delete ALL data in the vector database including:\n\n• All discovered devices\n• All applications\n• All relationships\n• All configurations\n• All inventory data\n\nThis action cannot be undone. Are you sure you want to continue?')) {
            return;
        }

        try {
            // Disable button and show loading state
            button.disabled = true;
            button.textContent = '⏳ Clearing...';

            console.log('✅ Calling vector_db clear_all_data...');

            // Call the vector_db canister's clear_all_data function via canisterClient
            const canisterClient = window.canisterClient || window.appCore?.getModule('canisterClient');
            if (!canisterClient) {
                throw new Error('CanisterClient not available');
            }

            const result = await canisterClient.callCanister('vector_db', 'clear_all_data', []);

            console.log('✅ Clear result:', result);

            // Extract message from result
            let message = 'Database cleared successfully';

            if (result && typeof result === 'object') {
                if ('ok' in result) {
                    message = result.ok || message;
                } else if ('Ok' in result) {
                    message = result.Ok || message;
                } else if ('err' in result || 'Err' in result) {
                    const error = result.err || result.Err;
                    console.error('❌ Error from canister:', error);
                    throw new Error(error);
                }
            }

            // Show success message
            if (window.appCore?.getModule('toastNotifications')) {
                window.appCore.getModule('toastNotifications').show('✅ ' + message, 'success');
            }

            // Also show alert for confirmation
            alert('✅ Database cleared successfully!\n\n' + message);

            // Refresh vector search stats
            if (window.appCore?.getModule('vectorSearch')?.loadDatabaseStats) {
                window.appCore.getModule('vectorSearch').loadDatabaseStats();
            }

            // Refresh dashboard stats if on dashboard page
            if (window.appCore?.getModule('pageManager')?.updateDashboardStats) {
                window.appCore.getModule('pageManager').updateDashboardStats();
            }

        } catch (error: any) {
            console.error('Error clearing vector database:', error);
            if (window.appCore?.getModule('toastNotifications')) {
                window.appCore.getModule('toastNotifications').show('❌ Error clearing database: ' + error.message, 'error');
            }
            alert('❌ Error clearing database: ' + error.message);
        } finally {
            // Re-enable button
            button.disabled = false;
            button.textContent = '🗑️ Clear Database';
        }
    };

    window.sendAIMessage = async function(): Promise<void> {
        console.log('Sending AI message...');
        const input = document.getElementById('ai-input') as HTMLInputElement;
        const chatContainer = document.getElementById('ai-messages');
        const welcomeMsg = document.getElementById('ai-welcome');

        if (!input || !chatContainer) {
            console.error('Required elements not found:', { input: !!input, chatContainer: !!chatContainer });
            return;
        }

        const message = input.value.trim();
        if (!message) return;

        // Hide welcome message if it's the first message
        if (welcomeMsg) {
            welcomeMsg.style.display = 'none';
        }

        // Clear input
        input.value = '';

        // Add user message to chat
        const userMsgDiv = document.createElement('div');
        userMsgDiv.className = 'mb-4';
        userMsgDiv.innerHTML = `
            <div class="flex items-start gap-2">
                <div class="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">U</div>
                <div class="flex-1">
                    <div class="text-sm text-gray-600 mb-1">You</div>
                    <div class="bg-gray-100 rounded-lg p-3">${escapeHtml(message)}</div>
                </div>
            </div>
        `;
        chatContainer.appendChild(userMsgDiv);

        // Show loading state
        const loadingDiv = document.createElement('div');
        loadingDiv.className = 'mb-4 ai-loading';
        loadingDiv.innerHTML = `
            <div class="flex items-start gap-2">
                <div class="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-medium">AI</div>
                <div class="flex-1">
                    <div class="text-sm text-gray-600 mb-1">AI Assistant</div>
                    <div class="bg-blue-50 rounded-lg p-3">
                        <div class="flex items-center gap-2">
                            <div class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                            <div class="w-2 h-2 bg-blue-600 rounded-full animate-pulse" style="animation-delay: 0.2s"></div>
                            <div class="w-2 h-2 bg-blue-600 rounded-full animate-pulse" style="animation-delay: 0.4s"></div>
                        </div>
                    </div>
                </div>
            </div>
        `;
        chatContainer.appendChild(loadingDiv);

        // Scroll to bottom
        const scrollContainer = document.getElementById('ai-conversation');
        if (scrollContainer) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }

        try {
            // Get AI response from Ada canister
            let aiResponse: string;

            // Ensure we have a conversation ID
            if (!window.adaConversationId) {
                const convResult = await callCanister<{ Ok?: string; Err?: string }>(
                    'ada', 'start_conversation', ['AI Assistant Session']
                );
                if (convResult.Ok) {
                    window.adaConversationId = convResult.Ok;
                }
            }

            // Send message to Ada
            if (window.adaConversationId) {
                const msgResult = await callCanister<{ Ok?: any; Err?: string }>(
                    'ada', 'send_message', [window.adaConversationId, message]
                );

                console.log('Ada response:', msgResult);

                if (msgResult.Ok) {
                    const responseData = msgResult.Ok;
                    // AgentAdapter returns native JS objects with typed fields
                    if (typeof responseData === 'object' && responseData.message) {
                        aiResponse = formatMarkdownToHtml(responseData.message);
                    } else if (typeof responseData === 'string') {
                        aiResponse = formatMarkdownToHtml(responseData);
                    } else {
                        aiResponse = 'Unexpected response format. Please try again.';
                        console.error('Unknown response format:', typeof responseData, responseData);
                    }
                } else if (msgResult.Err) {
                    aiResponse = `<span class="text-red-600">Error: ${msgResult.Err}</span>`;
                } else {
                    aiResponse = 'Unexpected response structure. Please try again.';
                }
            } else {
                aiResponse = 'Failed to start conversation. Please reload the page and try again.';
            }

            // Remove loading div
            loadingDiv.remove();

            // Add AI response to chat
            const aiMsgDiv = document.createElement('div');
            aiMsgDiv.className = 'mb-4';
            aiMsgDiv.innerHTML = `
                <div class="flex items-start gap-2">
                    <div class="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-medium">AI</div>
                    <div class="flex-1">
                        <div class="text-sm text-gray-600 mb-1">AI Assistant</div>
                        <div class="bg-blue-50 rounded-lg p-3">${aiResponse}</div>
                    </div>
                </div>
            `;
            chatContainer.appendChild(aiMsgDiv);

            // Scroll to bottom
            const scrollContainer2 = document.getElementById('ai-conversation');
            if (scrollContainer2) {
                scrollContainer2.scrollTop = scrollContainer2.scrollHeight;
            }

        } catch (error) {
            console.error('AI processing error:', error);
            loadingDiv.remove();

            const errorDiv = document.createElement('div');
            errorDiv.className = 'mb-4';
            errorDiv.innerHTML = `
                <div class="flex items-start gap-2">
                    <div class="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-medium">!</div>
                    <div class="flex-1">
                        <div class="text-sm text-gray-600 mb-1">Error</div>
                        <div class="bg-red-50 rounded-lg p-3 text-red-600">
                            Failed to process your request. Please try again.
                        </div>
                    </div>
                </div>
            `;
            chatContainer.appendChild(errorDiv);
        }
    };

    window.clearAIConversation = function(): void {
        console.log('Clearing AI conversation...');
        const container = document.getElementById('ai-messages');
        const welcomeMsg = document.getElementById('ai-welcome');
        if (container) {
            container.innerHTML = '';
        }
        // Show welcome message again
        if (welcomeMsg) {
            welcomeMsg.style.display = 'block';
        }
        // Reset Ada conversation so a new one starts
        window.adaConversationId = null;
        console.log('✅ Ada conversation reset');
    };

    window.editSchedule = function(id: string): void {
        console.log('editSchedule stub called, waiting for app.js to load...', id);
    };

    window.deleteSchedule = function(id: string): void {
        console.log('deleteSchedule stub called, waiting for app.js to load...', id);
    };

    window.saveSchedule = function(): void {
        console.log('Saving schedule...');
        window.hideScheduleModal();
        alert('Schedule saved successfully');
    };

    console.log('✅ Window functions initialized');
}

// Helper functions (private to this module)
function parseDevicesFromRaw(raw: string): any[] {
    const devices: any[] = [];
    const deviceRegex = /record\s*\{\s*"([^"]+)";\s*vec\s*\{([\s\S]*?)\};\s*\}/g;
    let match;

    while ((match = deviceRegex.exec(raw)) !== null) {
        const deviceId = match[1];
        const metadataStr = match[2];
        const device: any = { device_id: deviceId };

        const kvRegex = /record\s*\{\s*"([^"]+)";\s*"([^"]*)"\s*\}/g;
        let kvMatch;

        while ((kvMatch = kvRegex.exec(metadataStr)) !== null) {
            device[kvMatch[1]] = kvMatch[2];
        }

        devices.push(device);
    }

    console.log(`📊 Export parsed ${devices.length} devices from Candid`);
    return devices;
}

function escapeCSV(str: string | null | undefined): string {
    if (str === null || str === undefined) return '';
    const s = String(str);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

function formatMarkdownToHtml(text: string): string {
    return text
        // Headers
        .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mt-4 mb-2">$1</h2>')
        .replace(/^### (.+)$/gm, '<h3 class="text-md font-semibold mt-3 mb-2">$1</h3>')
        // Bold
        .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
        // Lists
        .replace(/^• (.+)$/gm, '<li class="ml-4">$1</li>')
        .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 list-decimal">$2</li>')
        // Code blocks
        .replace(/`([^`]+)`/g, '<code class="bg-gray-200 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
        // Line breaks
        .replace(/\n/g, '<br>');
}

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

export { parseDevicesFromRaw, escapeCSV, formatMarkdownToHtml, escapeHtml };
